import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../lib/utils';
import NotificationBell from './NotificationBell';
import {
  LayoutDashboard,
  Users,
  LogOut,
  Menu,
  X,
  GanttChart,
  ListTodo,
  FolderKanban,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  Settings,
  BarChart3,
  Receipt,
  ClipboardCheck,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ticketing-hub-18/artifacts/9kyejo6n_Screenshot%202026-04-08%20at%205.02.54%E2%80%AFPM.png";

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['user', 'support', 'pm', 'developer', 'admin', 'super_admin'] },
  { path: '/work-items', icon: ListTodo, label: 'Planboard', roles: ['pm', 'developer', 'admin', 'super_admin'] },
  { path: '/kanban', icon: FolderKanban, label: 'Kanban Board', roles: ['pm', 'developer', 'admin', 'super_admin'] },
  { path: '/projects', icon: GanttChart, label: 'Projects', roles: ['pm', 'admin', 'super_admin'] },
  { path: '/resource-utilization', icon: BarChart3, label: 'Utilization', roles: ['admin', 'super_admin', 'pm'] },
  { path: '/project-billing', icon: Receipt, label: 'Billing', roles: ['admin', 'super_admin', 'pm'] },
  { path: '/approvals', icon: ClipboardCheck, label: 'Approvals', roles: ['admin', 'super_admin', 'pm'] },
  { path: '/project-team', icon: Users, label: 'Teams', roles: ['admin', 'super_admin', 'pm'] },
  { path: '/organizations', icon: Building2, label: 'Organizations', roles: ['admin', 'super_admin'] },
  { path: '/users', icon: Users, label: 'Users', roles: ['admin', 'super_admin', 'pm'] },
  { path: '/settings', icon: Settings, label: 'Settings', roles: ['admin', 'super_admin', 'pm'] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role || 'user'));

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded-md text-slate-700"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex items-center gap-2 ml-3">
            <img src={LOGO_URL} alt="Intelvolks" className="h-6" />
            <span className="font-heading font-bold text-slate-800 text-base">Tasks</span>
          </div>
        </div>
        <NotificationBell />
      </header>

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'open' : ''} ${collapsed ? 'sidebar--collapsed' : ''}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`h-14 flex items-center border-b border-slate-200 ${collapsed ? 'lg:justify-center lg:px-2 px-5 gap-2.5' : 'gap-2.5 px-5'}`}>
          <img src={LOGO_URL} alt="Intelvolks" className="h-6" data-testid="sidebar-logo" />
          {(!collapsed || sidebarOpen) && <span className="font-heading font-bold text-slate-800 text-base tracking-tight">Tasks</span>}
        </div>

        {/* User Info */}
        {(!collapsed || sidebarOpen) ? (
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-white bg-[#002FA7] text-xs flex-shrink-0">
                {getInitials(user?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate text-slate-800">{user?.name}</p>
                <p className="text-[10px] truncate capitalize text-slate-500">{user?.role}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-3 flex justify-center border-b border-slate-200">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-white bg-[#002FA7] text-xs">
              {getInitials(user?.name)}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-2 space-y-0.5 flex-1" data-testid="nav-menu">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              title={(collapsed && !sidebarOpen) ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  (collapsed && !sidebarOpen) ? 'justify-center' : ''
                } ${
                  isActive
                    ? 'bg-[#eef2ff] text-[#002FA7]'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
              {(!collapsed || sidebarOpen) && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle + Logout */}
        <div className="border-t border-slate-200 p-2 space-y-0.5">
          {/* Collapse button - desktop only */}
          <button
            className={`hidden lg:flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-md transition-colors ${collapsed ? 'justify-center' : ''}`}
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse-btn"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight className="h-[18px] w-[18px]" /> : <ChevronsLeft className="h-[18px] w-[18px]" />}
            {!collapsed && <span>Collapse</span>}
          </button>
          <button
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors ${(collapsed && !sidebarOpen) ? 'justify-center' : ''}`}
            onClick={handleLogout}
            data-testid="logout-btn"
            title={(collapsed && !sidebarOpen) ? 'Logout' : undefined}
          >
            <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
            {(!collapsed || sidebarOpen) && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`main-content ${collapsed ? 'main-content--collapsed' : ''} pt-14 lg:pt-0`}>
        {/* Desktop Top Bar */}
        <div className="hidden lg:flex items-center justify-end h-12 px-6 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="w-7 h-7 rounded-full flex items-center justify-center font-heading font-bold text-white bg-[#002FA7] text-[10px]">
              {getInitials(user?.name)}
            </div>
          </div>
        </div>
        <div className="p-5 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;